import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Paper, TableContainer, Collapse, useTheme } from '@mui/material';
import { CalendarHeader, CalendarMonthCards } from './CalendarHeatmap';

/**
 * Calendar + table block for the Reports tabs.
 *
 * - The calendar header stays fixed above the scroll area.
 * - The month cards live inside the scroll area and scroll away with the
 *   table. Once they're fully out of view (measured, not a fixed offset) the
 *   header arrow flips to "show", and clicking it scrolls back up.
 * - While the cards are visible, clicking the header hides them entirely so
 *   the table gets the whole height; clicking again brings them back.
 * - The cards are sticky horizontally (width = the container's visible width)
 *   so a wide table's horizontal scroll doesn't drag them sideways.
 *
 * Must be a direct child of a flex-column parent (e.g. ReportTabLayout): it
 * returns a fragment, and the scroll container relies on `flex: 1;
 * minHeight: 0` to be bounded by that parent. A sticky TableHead inside
 * `children` should use `top: 0`.
 */
const CalendarTableSection = ({
  startDate,
  endDate,
  data,
  selectedDay,
  onDayClick,
  onClearSelection,
  summary,
  containerId,
  children,
}) => {
  const theme = useTheme();
  const scrollContainerRef = useRef(null);
  const cardsRef = useRef(null);
  const cardsHeightRef = useRef(0);
  const [containerWidth, setContainerWidth] = useState('100%');
  const [isCardsOpen, setIsCardsOpen] = useState(true);
  const [isScrolledOut, setIsScrolledOut] = useState(false);

  const updateScrolledOut = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const cardsHeight = cardsHeightRef.current;
    // React skips the re-render when the boolean is unchanged.
    setIsScrolledOut(cardsHeight > 0 && el.scrollTop >= cardsHeight - 1);
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === scrollContainerRef.current) {
          setContainerWidth(`${entry.contentRect.width}px`);
        } else if (entry.target === cardsRef.current) {
          // Height changes with the range (weeks shown) and while collapsing.
          cardsHeightRef.current = entry.contentRect.height;
          updateScrolledOut();
        }
      }
    });

    if (scrollContainerRef.current) observer.observe(scrollContainerRef.current);
    if (cardsRef.current) observer.observe(cardsRef.current);
    return () => observer.disconnect();
  }, [updateScrolledOut]);

  const handleToggle = useCallback(() => {
    const el = scrollContainerRef.current;
    if (isScrolledOut || !isCardsOpen) {
      setIsCardsOpen(true);
      el?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsCardsOpen(false);
  }, [isScrolledOut, isCardsOpen]);

  return (
    <>
      {/* Calendar Header (Always Fixed) */}
      <Paper
        elevation={0}
        sx={{
          flexShrink: 0,
          width: '100%',
          minWidth: 0,
          borderRadius: 2,
          border: '1px solid ' + theme.palette.divider,
          borderBottom: 'none',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          bgcolor: 'background.paper',
          zIndex: 20
        }}
      >
        <CalendarHeader
          startDate={startDate}
          endDate={endDate}
          selectedDay={selectedDay}
          onClearSelection={onClearSelection}
          isExpanded={isCardsOpen && !isScrolledOut}
          onToggle={handleToggle}
          summary={summary}
        />
      </Paper>

      {/* INNER SCROLLABLE AREA */}
      <TableContainer
        id={containerId}
        component={Paper}
        elevation={0}
        ref={scrollContainerRef}
        onScroll={updateScrolledOut}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'auto',
          width: '100%',
          borderRadius: 2,
          border: '1px solid ' + theme.palette.divider,
          borderTop: 'none',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          bgcolor: 'background.paper',
          willChange: 'scroll-position',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: '8px',
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[100],
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: theme.palette.grey[500],
            },
          },
        }}
      >
        {/* Calendar Cards - Sticky Horizontally, Scrolls Vertically */}
        <Box
          ref={cardsRef}
          sx={{
            position: 'sticky',
            left: 0,
            width: containerWidth,
            zIndex: 11,
            bgcolor: 'background.paper',
            borderBottom: isCardsOpen ? `1px solid ${theme.palette.divider}` : 'none',
          }}
        >
          <Collapse in={isCardsOpen} timeout={200}>
            <CalendarMonthCards
              startDate={startDate}
              endDate={endDate}
              data={data}
              selectedDay={selectedDay}
              onDayClick={onDayClick}
            />
          </Collapse>
        </Box>

        {children}
      </TableContainer>
    </>
  );
};

export default CalendarTableSection;
